/**
 * RBAC-8.1 · A role's history, as diffs, and what restoring a version means (FR-26 to FR-28).
 *
 * The database keeps the FULL permission set per edit (`role_permission_versions`, `20261103a2`), not a
 * delta, so every diff here is between two complete sets and cannot drift from a missed event. A
 * version's predecessor is the next older one; the oldest is either the baseline (where the role stood
 * when history began, shown without a diff) or an edit on a role made after that, whose predecessor
 * really was the empty set.
 *
 * ⚠️ HISTORY IS TOLD IN THE SAVE FLOW'S WORDS. Each line is the RBAC-2.2 heading and RBAC-2.3's
 * consequence, put in the past by `consequenceThen()` with the holder count RECORDED WITH THE VERSION.
 *
 * ⚠️ A RESTORE IS AN ORDINARY SAVE. Its preview is `role_save_preview()` of the version's set and its
 * write is `role_save_permissions()`, both server side; `parseRestorePreview` reuses RBAC-3.6's parser
 * so the dialog renders the same object. `reserved` is advice for the dialog. The refusal is the
 * RBAC-1.5 trigger's, whatever this module says.
 *
 * Pure, no DOM, no Supabase.
 */

import { consequenceThen } from './permissionConsequences';
import { PERMISSION_AREAS, PERMISSION_DISPLAY } from './permissionDisplay';
import { listOf, parseRoleSaveDiff, permissionTitle, type DiffLine, type RoleSaveDiff } from './rolePermissionDiff';

export const ROLE_PERMISSION_HISTORY_RPC = 'role_permission_history';
export const ROLE_RESTORE_PREVIEW_RPC = 'role_restore_preview';
export const ROLE_RESTORE_VERSION_RPC = 'role_restore_version';

export type VersionSource = 'baseline' | 'edit' | 'restore';

export interface RoleVersion {
  id: number;
  recordedAt: string;
  actorId: string | null;
  actorName: string | null;
  source: VersionSource;
  restoredFrom: number | null;
  /** Holders when the version was written: both role systems, any account, active only. */
  holderCount: number;
  /** The full set after the edit, sorted. */
  permissions: string[];
}

export interface RoleHistory {
  roleId: number;
  roleName: string;
  /** The role's grants now. */
  current: string[];
  /** Newest first. */
  versions: RoleVersion[];
}

const isId = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isNames = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
const SOURCES: readonly VersionSource[] = ['baseline', 'edit', 'restore'];

function parseVersion(raw: unknown): RoleVersion | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isId(r.id) || typeof r.recorded_at !== 'string' || Number.isNaN(Date.parse(r.recorded_at))) return null;
  if (!SOURCES.includes(r.source as VersionSource) || !isCount(r.holder_count) || !isNames(r.permissions)) return null;
  if (r.actor_id !== null && typeof r.actor_id !== 'string') return null;
  if (r.actor_name !== null && typeof r.actor_name !== 'string') return null;
  if (r.restored_from !== null && !isId(r.restored_from)) return null;
  // Only a restore names what it restored.
  if ((r.restored_from !== null) !== (r.source === 'restore')) return null;
  return {
    id: r.id,
    recordedAt: r.recorded_at,
    actorId: r.actor_id as string | null,
    actorName: r.actor_name as string | null,
    source: r.source as VersionSource,
    restoredFrom: r.restored_from as number | null,
    holderCount: r.holder_count,
    permissions: [...r.permissions].sort(),
  };
}

export function parseRoleHistory(raw: unknown): RoleHistory | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isId(r.role_id) || typeof r.role_name !== 'string' || !isNames(r.current) || !Array.isArray(r.versions)) return null;
  const versions = r.versions.map(parseVersion);
  if (versions.some((v) => v === null)) return null;
  const vs = versions as RoleVersion[];
  // Newest first, as the server promises. A different order would pair each version with the wrong predecessor.
  for (let i = 1; i < vs.length; i++) if (vs[i].id >= vs[i - 1].id) return null;
  return { roleId: r.role_id, roleName: r.role_name, current: [...r.current].sort(), versions: vs };
}

const sameSet = (a: readonly string[], b: readonly string[]) => {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
};

const areaRank = (key: string) => {
  const a = PERMISSION_DISPLAY[key]?.area;
  const i = a ? PERMISSION_AREAS.indexOf(a) : -1;
  return i < 0 ? PERMISSION_AREAS.length : i;
};
const ordered = (keys: readonly string[]) =>
  [...keys].sort((a, b) => areaRank(a) - areaRank(b) || permissionTitle(a).localeCompare(permissionTitle(b)));

export type DateFormat = (iso: string) => string;

export const defaultDateFormat: DateFormat = (iso) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export interface HistoryEntry {
  version: RoleVersion;
  /** "Changed by Dana Ruiz", "Restored by …", "Where this role stood …". */
  heading: string;
  when: string;
  gained: DiffLine[];
  lost: DiffLine[];
  /** The baseline has no predecessor to differ from. */
  showsDiff: boolean;
  /** The role's grants are this version's set now. */
  isCurrent: boolean;
  /** Restoring it would change something. */
  restorable: boolean;
}

function who(v: RoleVersion): string {
  return v.actorId === null ? 'directly in the database, with nobody signed in' : `by ${v.actorName?.trim() || 'someone no longer listed'}`;
}

function heading(v: RoleVersion, byId: ReadonlyMap<number, RoleVersion>, fmt: DateFormat): string {
  switch (v.source) {
    case 'baseline':
      return 'Where this role stood when its history began';
    case 'restore': {
      const from = v.restoredFrom !== null ? byId.get(v.restoredFrom) : undefined;
      return `Restored ${who(v)} to the version of ${from ? fmt(from.recordedAt) : 'an earlier date'}`;
    }
    default:
      return `Changed ${who(v)}`;
  }
}

/** Newest first. Each entry is the change from the version below it. */
export function historyEntries(h: RoleHistory, fmt: DateFormat = defaultDateFormat): HistoryEntry[] {
  const byId = new Map(h.versions.map((v) => [v.id, v]));
  return h.versions.map((v, i) => {
    const before = h.versions[i + 1]?.permissions ?? [];
    const showsDiff = v.source !== 'baseline';
    const was = new Set(before);
    const now = new Set(v.permissions);
    const n = v.holderCount;
    const line = (dir: 'gained' | 'lost') => (k: string): DiffLine => ({ key: k, title: permissionTitle(k), sentence: consequenceThen(k, dir, n) });
    return {
      version: v,
      heading: heading(v, byId, fmt),
      when: fmt(v.recordedAt),
      gained: showsDiff ? ordered(v.permissions.filter((k) => !was.has(k))).map(line('gained')) : [],
      lost: showsDiff ? ordered(before.filter((k) => !now.has(k))).map(line('lost')) : [],
      showsDiff,
      isCurrent: i === 0 && sameSet(v.permissions, h.current),
      restorable: !sameSet(v.permissions, h.current),
    };
  });
}

/** The baseline's one line, so it does not render as an empty change. */
export function baselineSummary(v: RoleVersion): string {
  const n = v.permissions.length;
  const held = v.holderCount === 1 ? '1 person held it' : v.holderCount === 0 ? 'nobody held it' : `${v.holderCount} people held it`;
  return `${n === 1 ? '1 permission' : `${n} permissions`}, and ${held}.`;
}

export interface RestorePreview {
  versionId: number;
  recordedAt: string;
  /** The diff, exactly `role_save_preview()`'s. */
  diff: RoleSaveDiff;
  /** Reserved permissions in the diff the caller will be refused on. Empty for a superadmin. */
  reserved: string[];
}

export function parseRestorePreview(raw: unknown): RestorePreview | null {
  const diff = parseRoleSaveDiff(raw);
  if (!diff || diff.saved) return null;
  const r = raw as Record<string, unknown>;
  if (!isId(r.version_id) || typeof r.recorded_at !== 'string' || !isNames(r.reserved)) return null;
  // A reserved permission the restore would be refused on is one the restore changes.
  if (!r.reserved.every((k) => diff.gaining.includes(k) || diff.losing.includes(k))) return null;
  return { versionId: r.version_id, recordedAt: r.recorded_at, diff, reserved: r.reserved };
}

/** Said before the confirm, so the refusal is not a surprise. The refusal itself is the server's. */
export function reservedWarning(reserved: readonly string[]): string | null {
  if (reserved.length === 0) return null;
  const names = listOf(ordered(reserved).map((k) => `“${permissionTitle(k)}”`));
  return `This version would change ${names}, which only a Joules superadmin may grant or remove, so restoring it will be refused. Ask Joules support, or make the other changes by hand.`;
}

/** Arguments for `role_restore_version`. `diff.holders.count` is the number on the button. */
export function restoreRoleArgs(roleId: number, versionId: number, base: readonly string[], diff: Pick<RoleSaveDiff, 'holders'>) {
  return {
    p_role_id: roleId,
    p_version_id: versionId,
    p_base_permissions: [...new Set(base)].sort(),
    p_expected_holders: diff.holders.count,
  };
}
